/**
 * Projection de confettis (fond de section), une fois à l’entrée dans le viewport.
 */
const CONFETTI_COLORS = [
  "#e63946",
  "#f4a261",
  "#2a9d8f",
  "#457b9d",
  "#e9c46a",
  "#9b5de5",
  "#ff006e",
  "#8338ec",
];

class LayerImageConfetti extends HTMLElement {
  constructor() {
    super();
    this._onResize = this._onResize.bind(this);
    this._tick = this._tick.bind(this);
    this._onIntersect = this._onIntersect.bind(this);
    this._played = false;
    this._raf = null;
    this._particles = [];
    this._spawnEndTime = 0;
    /** Reste fractionnaire pour émettre au moins quelques confettis quand le débit < 1 / frame */
    this._spawnAccumulator = 0;
  }

  connectedCallback() {
    this._canvas = this.querySelector("canvas");
    if (!this._canvas) return;

    this._ctx = this._canvas.getContext("2d", { alpha: true });
    this._prefersReduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._resizeObserver = new ResizeObserver(() => this._onResize());
    this._resizeObserver.observe(this);

    this._origin = this.dataset.origin || "center";
    this._direction = this.dataset.direction || "up";
    const rawShape = (this.dataset.shape || "square").toLowerCase();
    this._shape = ["square", "streamer", "heart"].includes(rawShape)
      ? rawShape
      : "square";
    this._intensity = Math.min(
      100,
      Math.max(1, Number(this.dataset.intensity) || 50)
    );
    const sec = Number(this.dataset.durationSec);
    this._durationMs = Math.max(
      500,
      (Number.isFinite(sec) ? sec : 3) * 1000
    );

    this._observer = new IntersectionObserver(this._onIntersect, {
      root: null,
      threshold: 0.15,
      rootMargin: "0px",
    });
    this._observer.observe(this);
    this._onResize();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  _onIntersect(entries) {
    const e = entries[0];
    if (!e || !e.isIntersecting) return;
    this._observer.disconnect();
    if (this._prefersReduced) return;
    if (this._played) return;
    this._played = true;
    this._startBurst();
  }

  _onResize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.clientWidth;
    const h = this.clientHeight;
    if (w < 1 || h < 1) return;
    this._canvas.width = Math.floor(w * dpr);
    this._canvas.height = Math.floor(h * dpr);
    this._canvas.style.width = `${w}px`;
    this._canvas.style.height = `${h}px`;
    if (this._ctx) this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._width = w;
    this._height = h;
  }

  _spawnRegion() {
    const w = this._width;
    const margin = w * 0.08;
    let xMin = margin;
    let xMax = w - margin;
    if (this._origin === "left") {
      xMax = w * 0.28;
      xMin = margin * 0.5;
    } else if (this._origin === "right") {
      xMin = w * 0.72;
      xMax = w - margin * 0.5;
    } else {
      xMin = w * 0.36;
      xMax = w * 0.64;
    }
    return { xMin, xMax };
  }

  _spawnParticle() {
    const { xMin, xMax } = this._spawnRegion();
    const x = xMin + Math.random() * (xMax - xMin);
    const up = this._direction === "up";
    const y = up ? this._height + 4 : -4;
    const speed = 6 + (this._intensity / 100) * 10;
    const spread = 3 + (this._intensity / 100) * 5;
    const vx = (Math.random() - 0.5) * spread * 2;
    const vy = up ? -(speed + Math.random() * 4) : speed + Math.random() * 4;

    const shape = this._shape;
    let w;
    let h;
    let vr = (Math.random() - 0.5) * 0.35;

    if (shape === "streamer") {
      w = 20 + Math.random() * 16;
      h = 2.2 + Math.random() * 2.2;
      vr = (Math.random() - 0.5) * 0.55;
    } else if (shape === "heart") {
      w = 9 + Math.random() * 9;
      h = w;
    } else {
      w = 4 + Math.random() * 5;
      h = 6 + Math.random() * 8;
    }

    this._particles.push({
      x,
      y,
      vx,
      vy,
      shape,
      color: CONFETTI_COLORS[(Math.random() * CONFETTI_COLORS.length) | 0],
      rot: Math.random() * Math.PI * 2,
      vr,
      w,
      h,
      life: 1,
    });
  }

  /**
   * Dessine la forme du confetti (carré, serpentin ou cœur) centrée sur l’origine.
   */
  _drawParticleShape(ctx, p) {
    const a = Math.max(0, Math.min(1, p.life));
    ctx.globalAlpha = a;
    ctx.fillStyle = p.color;

    if (p.shape === "streamer") {
      ctx.beginPath();
      const r = Math.min(p.h * 0.5, 2);
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(-p.w / 2, -p.h / 2, p.w, p.h, r);
      } else {
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      }
      ctx.fill();
      return;
    }

    if (p.shape === "heart") {
      const s = p.w;
      ctx.translate(0, -s * 0.5);
      ctx.beginPath();
      ctx.moveTo(0, s * 0.35);
      ctx.bezierCurveTo(0, 0, -s * 0.5, 0, -s * 0.5, s * 0.25);
      ctx.bezierCurveTo(-s * 0.5, s * 0.55, 0, s, 0, s);
      ctx.bezierCurveTo(0, s, s * 0.5, s * 0.55, s * 0.5, s * 0.25);
      ctx.bezierCurveTo(s * 0.5, 0, 0, 0, 0, s * 0.35);
      ctx.closePath();
      ctx.fill();
      return;
    }

    ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
  }

  _startBurst() {
    this._onResize();
    const start = performance.now();
    this._spawnEndTime = start + this._durationMs;
    this._spawnAccumulator = 0;
    // Cadence de base par frame ; l’accumulateur évite que Math.floor tombe à 0 dès que l’intensité < ~50 %
    this._spawnBudget =
      0.12 + (this._intensity / 100) * 1.35;

    const loop = (now) => {
      this._tick(now);
      const stillSpawning = now < this._spawnEndTime;
      const hasParticles = this._particles.length > 0;
      if (stillSpawning || hasParticles) {
        this._raf = requestAnimationFrame(loop);
      }
    };
    this._raf = requestAnimationFrame(loop);
  }

  _tick(now) {
    const ctx = this._ctx;
    const w = this._width;
    const h = this._height;
    if (!ctx || w < 1) return;

    ctx.clearRect(0, 0, w, h);

    const gravity = 0.22 + (this._intensity / 100) * 0.12;
    const drag = 0.995;

    if (now < this._spawnEndTime) {
      const rate = this._spawnBudget * (1 + Math.random() * 0.4);
      this._spawnAccumulator += rate;
      let bursts = Math.floor(this._spawnAccumulator);
      this._spawnAccumulator -= bursts;
      for (let i = 0; i < bursts; i++) this._spawnParticle();
    }

    const next = [];
    for (const p of this._particles) {
      p.vy += this._direction === "up" ? gravity : gravity;
      p.vx *= drag;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      p.life -= 0.004;

      if (
        p.life > 0 &&
        p.x > -40 &&
        p.x < w + 40 &&
        p.y > -80 &&
        p.y < h + 80
      ) {
        next.push(p);
      }
    }
    this._particles = next;

    for (const p of this._particles) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      this._drawParticleShape(ctx, p);
      ctx.restore();
    }
  }
}

if (!customElements.get("layer-image-confetti")) {
  customElements.define("layer-image-confetti", LayerImageConfetti);
}
